import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { JmapClient } from './lib/jmap.js';
import { CryptoClient } from './lib/crypto.js';
import { createApis, recoveryApiForAuth, totpRequired, totpVerify } from './lib/api.js';
import { useTheme } from './lib/theme.js';
import { useLocalKeystore } from './lib/keystore.js';
import * as keybackup from './lib/keybackup.js';
import * as sessionFlow from './lib/session-flow.js';
import { logout as apiLogout } from './lib/vault-api.js';
import { ToastProvider, useToast } from './components/ui.jsx';
import { AuthShell } from './pages/AuthShell.jsx';
import { Login } from './pages/Login.jsx';
import { Register } from './pages/Register.jsx';
import { FindAddress } from './pages/FindAddress.jsx';
import { Shell } from './pages/Shell.jsx';

// Persisted session so a refresh does not log the user out.
//  - "Trust this device for 30 days" (remember=true) -> localStorage with a real
//    30-day expiry.
//  - unchecked -> sessionStorage, cleared when the tab/window closes.
// Only the account email + its auth header are stored (never the raw password in
// a separate field); private keys stay in the local keystore (IndexedDB).
const SESSION_KEY = 'km-session';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const SESSION = {
  save(data, remember) {
    try {
      sessionStorage.removeItem(SESSION_KEY); localStorage.removeItem(SESSION_KEY);
      const store = remember ? localStorage : sessionStorage;
      store.setItem(SESSION_KEY, JSON.stringify({ ...data, remember: !!remember, exp: remember ? Date.now() + THIRTY_DAYS : 0 }));
    } catch { /* storage unavailable */ }
  },
  load() {
    try {
      const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (s.exp && Date.now() > s.exp) { this.clear(); return null; } // expired trust window
      return s.email && s.authHeader ? s : null;
    } catch { return null; }
  },
  clear() { try { localStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } },
};

// Root state machine: login | register | forgot | find | app.
export function App() {
  return <ToastProvider><Root /></ToastProvider>;
}

// Second-factor gate shown after a valid password when the account has TOTP on.
function TotpGate({ email, error, onSubmit, onCancel }) {
  const [code, setCode] = useState('');
  return (
    <form className="km-auth-form" onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
      <div><div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>Two-factor authentication</div><div className="km-muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>Enter the 6-digit code from your authenticator app for {email}.</div></div>
      <label className="km-field">Authenticator code<input className="km-input mono" autoFocus inputMode="numeric" placeholder="000 000" value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))} /></label>
      {error && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{error}</div>}
      <button className="km-btn" type="submit" disabled={code.replace(/\D/g, '').length !== 6}>Verify and sign in</button>
      <button type="button" className="km-btn-ghost" onClick={onCancel}>Back</button>
    </form>
  );
}

// Shown ONCE right after a vault is first created (register / migrate): the
// recovery code that restores access if the password is forgotten. Zero-knowledge:
// we never see or store it in plaintext, and cannot reset it.
function RecoveryCodeModal({ code, onDone }) {
  const [saved, setSaved] = useState(false);
  const download = () => {
    const body = `KryptaMail recovery code\n\nKeep this safe and private. It can unlock your encrypted mailbox if you forget your password. We cannot recover it for you.\n\n${code}\n`;
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    a.download = 'kryptamail-recovery-code.txt'; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,18,30,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
      <div className="km-card" style={{ maxWidth: 460, width: '100%', padding: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-.02em' }}>Save your recovery code</div>
        <p className="km-muted" style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>This is the only way to unlock your encrypted mail if you forget your password. We never see it and cannot reset it. Store it somewhere safe.</p>
        <div className="km-mono" style={{ fontSize: 16, letterSpacing: '.04em', border: '1px solid var(--km-line2)', borderRadius: 10, padding: '14px 16px', margin: '14px 0', wordBreak: 'break-all', textAlign: 'center' }}>{code}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="km-btn-outline" style={{ flex: 1 }} onClick={() => navigator.clipboard?.writeText(code)}>Copy</button>
          <button type="button" className="km-btn-outline" style={{ flex: 1 }} onClick={download}>Download</button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '14px 0 10px', cursor: 'pointer' }}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I have saved my recovery code
        </label>
        <button className="km-btn" style={{ width: '100%' }} disabled={!saved} onClick={onDone}>Continue</button>
      </div>
    </div>
  );
}

// Forgot password: unlock with the recovery code and set a new password. The
// master key is unchanged, so existing encrypted mail stays readable.
function ForgotVault({ initialEmail, error, busy, onSubmit, go }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const ready = email.includes('@') && code.trim().length >= 10 && pw.length >= 8 && pw === confirm && !busy;
  return (
    <form className="km-auth-form" onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit({ email: email.trim(), code: code.trim(), newPassword: pw }); }}>
      <div><div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>Reset with recovery code</div><div className="km-muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>Enter your recovery code to unlock your keys and set a new password. Your existing encrypted mail stays readable.</div></div>
      <label className="km-field">Email<input className="km-input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dscrypta.com" /></label>
      <label className="km-field">Recovery code<input className="km-input mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXXX-XXXXX-..." /></label>
      <label className="km-field">New password<input className="km-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" /></label>
      <label className="km-field">Confirm new password<input className="km-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></label>
      {confirm && pw !== confirm && <div style={{ color: 'var(--km-warn)', fontSize: 13 }}>Passwords do not match.</div>}
      {error && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{error}</div>}
      <button className="km-btn" type="submit" disabled={!ready}>{busy ? 'Recovering…' : 'Unlock and set new password'}</button>
      <button type="button" className="km-btn-ghost" onClick={() => go('login')}>Back to sign in</button>
    </form>
  );
}

function Root() {
  const theme = useTheme('light');
  const flash = useToast();
  const keystore = useLocalKeystore();
  const [screen, setScreen] = useState({ name: 'login', email: '' });
  const [session, setSession] = useState(null); // { jmap, crypto, email, apis }
  // Start in "restoring" when a persisted session exists, so a refresh goes
  // straight to a splash (never a flash of the login page) while we reconnect.
  const [restoring, setRestoring] = useState(() => !!SESSION.load());
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [totpStep, setTotpStep] = useState(null); // { email, parts, recoveryCode, remember } when 2FA is required
  const [recoveryCode, setRecoveryCode] = useState(null); // show-once after a vault is created
  const [forgotBusy, setForgotBusy] = useState(false);
  const recoveryApi = useMemo(() => recoveryApiForAuth(), []);

  useEffect(() => { recoveryApi.registration().then(setRegistration).catch(() => setRegistration({ enabled: false })); }, [recoveryApi]);

  const go = useCallback((name, extra = {}) => { setError(null); setScreen({ name, ...extra }); }, []);

  // Build the full session from unlocked vault parts (mail credential + PQC keys).
  // The mail credential (not the password) authenticates to Stalwart.
  const buildSession = useCallback(async (email, parts, remember) => {
    const authHeader = sessionFlow.basicAuth(email, parts.mailCred);
    const jmap = new JmapClient({ baseUrl: '', authHeader });
    await jmap.connect();
    const crypto = new CryptoClient();
    const apis = createApis({ jmap, authHeader });
    const self = await crypto.lookupRecipient(email).catch(() => null);
    const identity = parts.pqc ? { kem: { privateKey: parts.pqc.kem }, sig: { privateKey: parts.pqc.sig } } : null;
    keystore.saveSession(email, { pqc: parts.pqc, mailCred: parts.mailCred, loginKey: parts.loginKey });
    SESSION.save({ email }, remember);
    return { jmap, crypto, email, apis, keystore, identity, token: parts.token, loginKey: parts.loginKey, fingerprint: self?.fingerprint || null };
  }, [keystore]);

  // Recover the account's existing PQC keys for the one-time migration: device-local
  // first, then the legacy escrow (unwrapped with the password), else provision fresh.
  const recoverKeys = useCallback(async (email, password, basic) => {
    const local = keystore.identity(email);
    if (local) return local;
    try { const blob = await keybackup.fetchBackup(basic); if (blob) return await keybackup.unwrapKeys(blob, password); } catch { /* fall through */ }
    const prov = await new CryptoClient().provisionIdentity(email);
    return prov.privateKeys;
  }, [keystore]);

  // Verify a password against Stalwart - used only to gate the one-time migration.
  const validateBasic = useCallback(async (basic) => {
    try { await new JmapClient({ baseUrl: '', authHeader: basic }).connect(); return true; } catch { return false; }
  }, []);

  // Enter the app with unlocked parts; show the recovery code once if just created.
  const finalize = useCallback(async (email, parts, recCode, remember) => {
    setSession(await buildSession(email, parts, remember));
    if (recCode) setRecoveryCode(recCode);
  }, [buildSession]);

  const handleLogin = useCallback(async ({ email, password, remember }) => {
    setError(null);
    try {
      const r = await sessionFlow.login(email, password, validateBasic);
      let parts, recCode = null;
      if (r.unlocked) parts = r.unlocked;
      else { // password correct but no vault yet -> one-time migration / first setup
        const pqc = await recoverKeys(email, password, r.needsBootstrap.basic);
        const bs = await sessionFlow.bootstrap(email, password, r.needsBootstrap.basic, pqc);
        parts = bs; recCode = bs.recoveryCode;
      }
      if (await totpRequired(email)) { setTotpStep({ email, parts, recoveryCode: recCode, remember }); return; }
      await finalize(email, parts, recCode, remember);
      flash(recCode ? 'Account secured' : 'Keys unlocked');
    } catch (e) { setError(e.message || 'Login failed'); }
  }, [validateBasic, recoverKeys, finalize, flash]);

  const submitTotp = useCallback(async (code) => {
    if (!totpStep) return; setError(null);
    try {
      if (!(await totpVerify(totpStep.email, code))) { setError('Invalid authenticator code'); return; }
      await finalize(totpStep.email, totpStep.parts, totpStep.recoveryCode, totpStep.remember);
      setTotpStep(null); flash('Signed in');
    } catch (e) { setError(e.message || 'Login failed'); }
  }, [totpStep, finalize, flash]);

  const submitForgot = useCallback(async ({ email, code, newPassword }) => {
    setError(null); setForgotBusy(true);
    try {
      const parts = await sessionFlow.recoverWithCode(email, code, newPassword);
      await finalize(email, parts, null, true);
      flash('Recovered. New password set.');
    } catch (e) { setError(e.message || 'Recovery failed'); }
    finally { setForgotBusy(false); }
  }, [finalize, flash]);

  // Restore a persisted session on refresh from the device-local cache (no password
  // needed); the one-way login key silently re-mints a fresh kmapi session token.
  useEffect(() => {
    const saved = SESSION.load();
    if (!saved) { setRestoring(false); return; }
    let cancelled = false;
    (async () => {
      const cached = keystore.session(saved.email);
      if (!cached || !cached.mailCred) return; // nothing cached on this device -> show login
      const token = cached.loginKey ? await sessionFlow.sessionFromLoginKey(saved.email, cached.loginKey).catch(() => null) : null;
      const s = await buildSession(saved.email, { mailCred: cached.mailCred, pqc: cached.pqc, loginKey: cached.loginKey, token }, saved.remember);
      if (!cancelled) setSession(s);
    })().catch(() => { SESSION.clear(); }).finally(() => { if (!cancelled) setRestoring(false); });
    return () => { cancelled = true; };
  }, [buildSession, keystore]);

  const handleRegistered = useCallback(async ({ email, password }) => { await handleLogin({ email, password, remember: true }); }, [handleLogin]);

  const handleLogout = useCallback(() => {
    if (session?.token) apiLogout(session.token);
    if (session?.email) keystore.clearSession(session.email);
    SESSION.clear(); setSession(null); setTotpStep(null); setRecoveryCode(null); setScreen({ name: 'login', email: '' });
  }, [session, keystore]);

  if (session) return (
    <>
      <Shell session={session} theme={theme} onLogout={handleLogout} />
      {recoveryCode && <RecoveryCodeModal code={recoveryCode} onDone={() => setRecoveryCode(null)} />}
    </>
  );

  // Restoring a saved session: show a splash, never a flash of the login page.
  if (restoring) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--km-bg)' }}>
      <img src="/brand/mark.svg" width="44" height="44" alt="KryptaMail" style={{ opacity: 0.9, animation: 'km-pulse 1.1s ease-in-out infinite' }} />
    </div>
  );

  // Second login factor: enter the authenticator code before the session is built.
  if (totpStep) return (
    <AuthShell theme={theme}>
      <TotpGate email={totpStep.email} error={error} onSubmit={submitTotp} onCancel={() => { setTotpStep(null); setError(null); }} />
    </AuthShell>
  );

  return (
    <AuthShell theme={theme}>
      {screen.name === 'login' && <Login onLogin={handleLogin} error={error} initialEmail={screen.email} go={go} onPasskey={() => flash('Passkey sign-in: wire to WebAuthn')} />}
      {screen.name === 'register' && <Register api={recoveryApi} crypto={new CryptoClient()} registration={registration} onDone={handleRegistered} go={go} />}
      {screen.name === 'forgot' && <ForgotVault initialEmail={screen.email} error={error} busy={forgotBusy} onSubmit={submitForgot} go={go} />}
      {screen.name === 'find' && <FindAddress api={recoveryApi} go={go} />}
    </AuthShell>
  );
}
